import React from 'react';
import { useSession } from '../contexts/SessionContext';
import { maskName, maskPhone, maskEmail, maskAccountNumber, formatCurrency } from '../utils/maskUtils';
import './DebtOverview.css';

/**
 * Displays debt settlement options and account summary.
 * Shows masked personal details (e.g., 'T**** M******', '***-***-5678')
 * when unverified, with a prompt to verify identity to reveal full details.
 * After verification, shows full details.
 */
export default function DebtOverview({ clientData, onVerifyClick }) {
  const { isVerified, clientName, clientFirstName, email, phone, unmaskedClientData } = useSession();

  if (!clientData) return null;

  // Use unmasked data from session if available (post-verification), otherwise use initial masked data
  const displayData = (isVerified && unmaskedClientData) ? unmaskedClientData : clientData;
  const accounts = displayData.accounts || [];
  const firstAccount = accounts[0];

  // Determine display details based on verification status
  const displayName = isVerified
    ? (unmaskedClientData?.clientName || clientName || clientData.clientName || 'Client')
    : maskName(clientData.clientName || 'Client Name');

  const displayEmail = isVerified
    ? (email || firstAccount?.email || 'Not on file')
    : null;
  const displayPhone = isVerified
    ? (phone || firstAccount?.phoneNumber || 'Not on file')
    : null;

  return (
    <div className="debt-overview" data-testid="debt-overview">
      {/* Client Identity Section */}
      <div className="debt-overview__identity">
        <div className="debt-overview__avatar" aria-hidden="true">
          {isVerified
            ? (displayName || 'C')[0].toUpperCase()
            : '?'}
        </div>
        <div className="debt-overview__client-info">
          <h2 className="debt-overview__name" data-testid="client-name">
            {displayName}
          </h2>
          <div className="debt-overview__masked-details">
            {!isVerified ? (
              <>
                <span className="masked-detail" data-testid="masked-phone">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  {maskPhone(firstAccount?.phoneNumber || clientData?.phone)}
                </span>
                <span className="masked-detail" data-testid="masked-email">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  {maskEmail(firstAccount?.email || clientData?.email)}
                </span>
              </>
            ) : (
              <>
                <span className="masked-detail masked-detail--revealed" data-testid="full-phone">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  {displayPhone}
                </span>
                <span className="masked-detail masked-detail--revealed" data-testid="full-email">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  {displayEmail}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Verification Prompt - shown when not verified */}
      {!isVerified && (
        <button
          className="debt-overview__verify-btn"
          onClick={onVerifyClick}
          data-testid="verify-prompt-btn"
          type="button"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 5-5 5 5 0 0 1 5 5v4"/>
          </svg>
          Verify your identity to see full details
        </button>
      )}

      {/* Balance Summary Card */}
      <div className="debt-overview__balance-card" data-testid="balance-card">
        <div className="balance-card__header">
          <span className="balance-card__label">Total Outstanding Balance</span>
          <span className="balance-card__amount" data-testid="total-balance">
            {formatCurrency(clientData.totalOutstandingBalance)}
          </span>
        </div>
        <div className="balance-card__stats">
          <div className="balance-card__stat">
            <span className="balance-card__stat-label">Accounts</span>
            <span className="balance-card__stat-value">{clientData.totalAccounts || 0}</span>
          </div>
          <div className="balance-card__stat">
            <span className="balance-card__stat-label">In Default</span>
            <span className="balance-card__stat-value">{clientData.accountsInDefault || 0}</span>
          </div>
          <div className="balance-card__stat">
            <span className="balance-card__stat-label">Days Overdue</span>
            <span className="balance-card__stat-value">{clientData.maxDaysInArrears || 0}</span>
          </div>
          <div className="balance-card__stat">
            <span className="balance-card__stat-label">Status</span>
            <span className="balance-card__stat-value balance-card__stat-value--status">
              {clientData.worstAgingCategory || 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Settlement Options */}
      <div className="debt-overview__settlement" data-testid="settlement-options">
        <h3 className="debt-overview__section-title">Settlement Options</h3>

        {accounts.map((account, index) => (
          <div key={account.loanApplicationId || index} className="settlement-option-card" data-testid={`settlement-option-${index}`}>
            <div className="settlement-option-card__header">
              <div className="settlement-option-card__type">
                {account.productType || 'Account'}
              </div>
              <div className={`settlement-option-card__status settlement-option-card__status--${(account.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                {account.status || 'Unknown'}
              </div>
            </div>

            <div className="settlement-option-card__account">
              <span className="settlement-option-card__account-label">Account</span>
              <span className="settlement-option-card__account-number" data-testid={`account-number-${index}`}>
                {isVerified
                  ? (account.accountNumber || 'N/A')
                  : maskAccountNumber(account.accountNumber || '')}
              </span>
            </div>

            <div className="settlement-option-card__amounts">
              <div className="settlement-option-card__amount-row">
                <span>Outstanding Balance</span>
                <strong>{formatCurrency(account.outstandingBalance)}</strong>
              </div>
              {account.settlementAmount != null && (
                <div className="settlement-option-card__amount-row settlement-option-card__amount-row--highlight">
                  <span>Settlement Amount</span>
                  <strong className="settlement-highlight">{formatCurrency(account.settlementAmount)}</strong>
                </div>
              )}
              {account.settlementDiscount != null && account.settlementDiscount > 0 && (
                <div className="settlement-option-card__savings">
                  Save {Math.round(account.settlementDiscount * 100)}% with full settlement
                </div>
              )}
              {account.settlementAmount == null && (
                <div className="settlement-option-card__amount-row settlement-option-card__amount-row--muted">
                  <span>Settlement</span>
                  <span className="settlement-unavailable">Contact us for options</span>
                </div>
              )}
            </div>

            <div className="settlement-option-card__details">
              <div className="settlement-option-card__detail">
                <span>Days in Arrears</span>
                <strong>{account.daysInArrears || 0}</strong>
              </div>
              <div className="settlement-option-card__detail">
                <span>Last Payment</span>
                <strong>{formatCurrency(account.lastPaymentAmount)}</strong>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Loyalty Reward */}
      {clientData.eligibleForLoyaltyReward && (
        <div className="debt-overview__loyalty" data-testid="loyalty-reward">
          <span className="debt-overview__loyalty-badge" aria-hidden="true">⭐</span>
          You may qualify for a <strong>R{clientData.loyaltyRewardAmount?.toLocaleString('en-ZA') || '0'}</strong> loyalty reward when you enter debt counselling. See the <strong>Debt Counselling</strong> section below for details.
        </div>
      )}

      {/* Urgency Notice */}
      {clientData.anyLegalAction && (
        <div className="notice notice--warning debt-overview__notice" data-testid="legal-warning">
          <strong>Important:</strong> Legal proceedings are active on your account.
          Settling or arranging payment now may help avoid further legal costs and protect your credit record.
        </div>
      )}

      {/* Total Amount Due Summary */}
      <div className="debt-overview__total" data-testid="total-due">
        <div className="total-due-card">
          <div className="total-due-card__row">
            <span>Total Amount Due (incl. interest &amp; fees)</span>
            <strong>{formatCurrency(clientData.totalAmountDue)}</strong>
          </div>
          {clientData.totalArrearsAmount > 0 && (
            <div className="total-due-card__row total-due-card__row--arrears">
              <span>Total Arrears</span>
              <strong>{formatCurrency(clientData.totalArrearsAmount)}</strong>
            </div>
          )}
          {clientData.totalInterestAccrued > 0 && (
            <div className="total-due-card__row total-due-card__row--sub">
              <span>Interest Accrued</span>
              <span>{formatCurrency(clientData.totalInterestAccrued)}</span>
            </div>
          )}
          {clientData.totalFeesCharged > 0 && (
            <div className="total-due-card__row total-due-card__row--sub">
              <span>Fees Charged</span>
              <span>{formatCurrency(clientData.totalFeesCharged)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
